import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { RunProgress } from '@shared/sandbox-schemas';

/** Run lifecycle phase — RunProgress.phase ('running' | 'complete' | 'errored') plus an 'idle' start. */
export type SandboxPhase = 'idle' | RunProgress['phase'];

/** A recommendation staged from Analyse for the operator to apply back in Config (FR-V003). */
export interface StagedRecommendation {
  title: string;
  body: string;
}

interface SandboxRunState {
  phase: SandboxPhase;
  runId: string | null;
  /** Set by Analyse's "Send to Config"; consumed (and cleared) by Config on mount. */
  stagedRecommendation: StagedRecommendation | null;
}

interface SandboxRunActions {
  setPhase: (phase: SandboxPhase) => void;
  setRunId: (id: string) => void;
  /** Stage a recommendation; the caller then navigates to Config. */
  stageRecommendation: (rec: StagedRecommendation) => void;
  /** Read + clear the staged recommendation (Config calls this on mount). */
  consumeStagedRecommendation: () => StagedRecommendation | null;
  reset: () => void;
}

const SandboxRunContext = createContext<(SandboxRunState & SandboxRunActions) | null>(null);

const INITIAL_STATE: SandboxRunState = {
  phase: 'idle',
  runId: null,
  stagedRecommendation: null,
};

export function SandboxRunProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SandboxRunState>(INITIAL_STATE);

  // Deep-link: open a past/headless run by `?runId=` (e.g. /admin/sandbox/test?runId=…).
  // The run lifecycle is otherwise in-memory only, so without this a completed run
  // (incl. one driven by scripts/run-sandbox-eval.ts) can't be viewed in the UI.
  // Resolve the run's REAL phase from the server so a still-running run does not unlock
  // Analyse prematurely (Analyse gates on phase === 'complete').
  useEffect(() => {
    const urlRunId = new URLSearchParams(window.location.search).get('runId');
    if (!urlRunId) return;
    setState((s) => ({ ...s, runId: urlRunId }));
    fetch(`/api/v1/admin/sandbox/runs/${urlRunId}/progress`, { credentials: 'include' })
      .then((r) => (r.ok ? (r.json() as Promise<{ phase?: SandboxPhase }>) : null))
      .then((d) => { if (d?.phase) setState((s) => ({ ...s, phase: d.phase! })); })
      .catch(() => { /* leave phase 'idle' on failure — tabs stay locked */ });
  }, []);

  function setPhase(phase: SandboxPhase) {
    setState((s) => ({ ...s, phase }));
  }

  function setRunId(id: string) {
    setState((s) => ({ ...s, runId: id }));
  }

  function stageRecommendation(rec: StagedRecommendation) {
    setState((s) => ({ ...s, stagedRecommendation: rec }));
  }

  // Returns the staged recommendation once, clearing it so it isn't re-applied.
  const stagedRef = state.stagedRecommendation;
  function consumeStagedRecommendation(): StagedRecommendation | null {
    if (!stagedRef) return null;
    setState((s) => ({ ...s, stagedRecommendation: null }));
    return stagedRef;
  }

  function reset() {
    setState(INITIAL_STATE);
  }

  return (
    <SandboxRunContext.Provider
      value={{ ...state, setPhase, setRunId, stageRecommendation, consumeStagedRecommendation, reset }}
    >
      {children}
    </SandboxRunContext.Provider>
  );
}

export function useSandboxRun() {
  const ctx = useContext(SandboxRunContext);
  if (!ctx) throw new Error('useSandboxRun must be used inside SandboxRunProvider');
  return ctx;
}
